import { Component, ChangeDetectionStrategy, ElementRef, OnInit, OnDestroy, AfterViewInit, viewChild, signal, inject, computed } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { PermissionService } from 'src/app/core/services/permission.service';
import { MenuService } from 'src/app/features/nav/menu.service';
import { ToastrService } from 'ngx-toastr';
import { getYear } from 'date-fns';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha-2';
import { getCurrencySymbol, CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';

import { AuthService } from './../auth.service';
import { AuthUserEntity } from './../../../types/AuthUser';
import { AppState } from '../../../state/app.reducer';
import { AuthActions } from '../state/action-typs';
import { NavHeaderService } from './../../../shared/services/nav-header.service';
import { UserDetailService } from 'src/app/shared/services/user-detail.service';
import { API } from 'src/app/shared/constants/APIConstants';
import { ListItemDialogComponent, ListItemDialogItem } from 'src/app/shared/components/competitions/dialogs/list-item-dialog/list-item-dialog.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiService } from 'src/app/core/services/ui.service';

@Component({
  selector: 'app-login',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TooltipModule, RecaptchaModule, RouterLink, ListItemDialogComponent, SharedModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  host: {
    // Classic pins the login to the dark theme; the new UI follows the app theme.
    '[attr.data-theme]': 'ui.isNew() ? null : "dark"'
  }
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly SUPER_USER_TYPE = 20;
  private readonly ADMIN_ROLE_TYPE = '2';
  private readonly ALLOWED_ROLES = ['2', '4', '6', '20', '30', '31', '32'];

  readonly videoPlayer = viewChild.required<ElementRef<HTMLVideoElement>>('videoPlayer');
  readonly captchaRef = viewChild.required<RecaptchaComponent>('captchaRef');

  protected readonly fb = inject(UntypedFormBuilder);
  protected readonly authService = inject(AuthService);
  protected readonly router = inject(Router);
  protected readonly store = inject(Store<AppState>);
  protected readonly navHeaderService = inject(NavHeaderService);
  protected readonly toastr = inject(ToastrService);
  protected readonly userDetailService = inject(UserDetailService);
  protected readonly http = inject(HttpClient);
  private readonly permissionService = inject(PermissionService);
  private readonly menuService = inject(MenuService);
  protected readonly destroy$ = new Subject<void>();
  protected readonly ui = inject(UiService);
  /** New UI only: reveals the typed password. */
  protected readonly showPassword = signal(false);

  loginForm!: UntypedFormGroup;
  protected allParentClub = signal<Array<any>>([]);
  protected readonly parentClubItems = computed<ListItemDialogItem[]>(() =>
    this.allParentClub().map(club => ({
      id: club.parentClubKey || club.emailId,
      title: club.name,
      subtitle: club.parentClubKey,
      emailId: club.emailId,
      password: club.password,
    })),
  );
  protected presentYear = signal<string>('');
  protected readonly showParentClubDialog = signal(false);
  protected isLoading = signal<boolean>(false);

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      emailId: ['', Validators.required],
      password: ['', Validators.required],
    });
    this.presentYear.set(getYear(new Date()).toString());
  }

  ngAfterViewInit(): void {
    const video = this.videoPlayer().nativeElement;
    if (video) {
      video.muted = true;
      video.play().catch(() => {});
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected loginThrough(emailId: string, password: string): void {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.http.post<any>(API.SECURED_LOGIN, { email: emailId, password })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.token && res.refreshToken) this.authService.setTokens(res.token, res.refreshToken);
          if (res.token) localStorage.setItem('SECURE_TOKEN', res.token);
          if (res.user?.parentClubId) localStorage.setItem('postGreParentClubId', res.user.parentClubId);
          const user: AuthUserEntity = {
            id: res.user.id, token: res.token, currency: '',
            emailId: res.user.email, name: res.user.name,
            roleType: res.user.roleType?.toString(), type: '',
            userType: [20, 21, 22, 23].includes(res.user.roleType) ? 20 : 0,
            parentClubLogo: '', parentClubKey: res.user.parentClubKey, isAllowForBooking: false
          };
          // Persist USER so JwtAuthInterceptor can attach Bearer tokens to subsequent requests
          localStorage.setItem('USER', JSON.stringify(user));

          // Platform users (no parentClubId, roles 20-23) go to super user dashboard
          if (!res.user.parentClubId && [20, 21, 22, 23].includes(res.user.roleType)) {
            this.handleSuperUserLogin(user);
          } else {
            this.handleParentClubLogin(user);
          }
        },
        error: (err) => this.handleLoginError(err)
      });
  }

  protected onSubmit(): void {
    if (this.isLoading()) return;
    if (this.loginForm.invalid) {
      this.toastr.error('Please fill in all required fields');
      return;
    }
    this.captchaRef().execute();
  }

  protected onCaptchaResolved(token: string | null): void {
    if (!token) return;
    this.isLoading.set(true);
    const emailId = this.loginForm.get('emailId')?.value;
    const password = this.loginForm.get('password')?.value.trim();
    this.http.post<any>(API.SECURED_LOGIN, { email: emailId, password })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.token && res.refreshToken) this.authService.setTokens(res.token, res.refreshToken);
          if (res.token) localStorage.setItem('SECURE_TOKEN', res.token);
          if (res.user?.parentClubId) localStorage.setItem('postGreParentClubId', res.user.parentClubId);
          const user: AuthUserEntity = {
            id: res.user.id, token: res.token, currency: '',
            emailId: res.user.email, name: res.user.name,
            roleType: res.user.roleType?.toString(), type: '',
            userType: [20, 21, 22, 23].includes(res.user.roleType) ? 20 : 0,
            parentClubLogo: '', parentClubKey: res.user.parentClubKey, isAllowForBooking: false
          };
          // Persist USER so JwtAuthInterceptor can attach Bearer tokens to subsequent requests
          localStorage.setItem('USER', JSON.stringify(user));
          this.handleUserLogin(user);
        },
        error: (err) => this.handleLoginError(err)
      });
  }

  protected onCaptchaError(error: any): void {
    this.isLoading.set(false);
    this.toastr.error('reCAPTCHA Timeout - Please check your network or domain configuration');
    this.captchaRef().reset();
  }

  protected onParentClubSelected(item: ListItemDialogItem): void {
    this.showParentClubDialog.set(false);
    this.loginThrough(item['emailId'], item['password']);
  }

  /**
   * Club logo lives on the `GET /parentclub/{id}` row as `ParentClubAppIconURL`.
   * That endpoint has been seen returning both the bare row and a `{ data: row }`
   * envelope (the sibling `clubName` read already guards for both), so accept
   * either shape rather than silently falling back to the generic app logo.
   */
  private readClubLogo(club: any): string {
    return (
      club?.ParentClubAppIconURL ||
      club?.parentClubAppIconURL ||
      club?.data?.ParentClubAppIconURL ||
      club?.data?.parentClubAppIconURL ||
      ''
    );
  }

  private handleLoginError(err: any): void {
    this.isLoading.set(false);
    this.toastr.error('Login failed');
    this.captchaRef().reset();
  }

  private handleParentClubLogin(user: AuthUserEntity): void {
    this.authService.getParentClubByFireBaseId(user.parentClubKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.loading && !res?.data) return;
          const postgreId = res?.data?.getParentClubByFireabseId?.Id;
          if (!postgreId) { this.handleLoginError(new Error('Unable to resolve parent club.')); return; }
          localStorage.setItem('postGreParentClubId', postgreId);
          this.authService.getParentClubByPostgreId(postgreId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (club: any) => {
                const code = club?.currency_code || 'GBP';
                user.currency = getCurrencySymbol(code, 'narrow');
                user.parentClubLogo = this.readClubLogo(club);
                localStorage.setItem('currencyCode', code);
                localStorage.setItem('clubName', club?.ParentClubName || club?.parentClubName || club?.data?.ParentClubName || club?.name || '');
                localStorage.setItem('USER', JSON.stringify(user));
                this.dispatchLoginAndNavigate(user);
              },
              error: () => {
                user.currency = '£';
                localStorage.setItem('currencyCode', 'GBP');
                this.dispatchLoginAndNavigate(user);
              }
            });
        },
        error: (err: any) => this.handleLoginError(err)
      });
  }

  private dispatchLoginAndNavigate(user: AuthUserEntity): void {
    this.store.dispatch(AuthActions.login({ authUser: user }));
    this.navHeaderService.toggleHeaderVisibility(true);

    // Load menu permissions (non-blocking, refresh sidebar once loaded)
    this.permissionService.loadPermissions(Number(user.roleType) || 2).then(() => {
      this.menuService.refreshMenus();
    });

    if (this.ALLOWED_ROLES.includes(String(user.roleType))) {
      this.router.navigateByUrl('/main/dashboard');
    } else {
      this.router.navigateByUrl('/main/dashboard/no-access');
    }
  }

  private handleSuperUserLogin(user: AuthUserEntity): void {
    // Clear club-specific fields — they are not valid until the super user selects a club
    user.parentClubKey = '';
    (user as any).parentClubId = '';
    localStorage.removeItem('postGreParentClubId');
    localStorage.setItem('USER', JSON.stringify(user));
    localStorage.setItem('superUser', 'true');
    localStorage.setItem('superUserId', user.id);
    localStorage.setItem('superUserName', user.name || '');
    localStorage.setItem('superUserRole', user.roleType || '20');
    localStorage.setItem('superUserToken', user.token || localStorage.getItem('SECURE_TOKEN') || '');
    this.navHeaderService.toggleHeaderVisibility(true);
    this.userDetailService._loggeedOnUserSubject.next(user);
    this.isLoading.set(false);
    this.router.navigateByUrl('/main/dashboard/superUserDashboardComponent');
  }

  private handleStandardUserLogin(user: AuthUserEntity): void {
    localStorage.removeItem('superUser');
    localStorage.removeItem('superUserId');

    this.authService.getParentClubByFireBaseId(user.parentClubKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.loading && !res?.data) return;
          const postgreId = res?.data?.getParentClubByFireabseId?.Id;
          if (!postgreId) { this.handleLoginError(new Error('Unable to resolve parent club.')); return; }
          localStorage.setItem('postGreParentClubId', postgreId);
          this.userDetailService.isSuperUserLoggedIn.next(false);
          this.navHeaderService.toggleHeaderVisibility(true);
          this.authService.getParentClubByPostgreId(postgreId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (club: any) => {
                const code = club?.currency_code || 'GBP';
                user.currency = getCurrencySymbol(code, 'narrow');
                user.parentClubLogo = this.readClubLogo(club);
                localStorage.setItem('currencyCode', code);
                localStorage.setItem('clubName', club?.ParentClubName || club?.parentClubName || club?.data?.ParentClubName || club?.name || '');
                localStorage.setItem('USER', JSON.stringify(user));
                this.store.dispatch(AuthActions.login({ authUser: user }));
                this.navigateBasedOnRole(user);
              },
              error: () => {
                user.currency = '£';
                localStorage.setItem('currencyCode', 'GBP');
                this.store.dispatch(AuthActions.login({ authUser: user }));
                this.navigateBasedOnRole(user);
              }
            });
        },
        error: (err: any) => this.handleLoginError(err)
      });
  }

  private navigateBasedOnRole(user: AuthUserEntity): void {
    // Load menu permissions then navigate to first permitted route
    this.permissionService.loadPermissions(Number(user.roleType) || 2).then(() => {
      this.menuService.refreshMenus();
      const landingRoute = this.permissionService.getFirstPermittedRoute();
      this.router.navigateByUrl(landingRoute);
    });

    // Immediate fallback navigation (before permissions load)
    if (this.ALLOWED_ROLES.includes(String(user.roleType))) {
      this.router.navigateByUrl('/main/dashboard');
    } else if (user.isAllowForBooking) {
      this.router.navigateByUrl('/main/court-booking');
    } else {
      this.router.navigateByUrl('/main/dashboard/no-access');
    }
  }

  private populateParentClubList(data: any, token: string): void {
    const clubs: any[] = [];
    data?.data?.forEach((element: any) => {
      clubs.push({
        id: '', token, currency: '',
        emailId: element.ParentClubAdminEmailID,
        name: element.ParentClubName,
        roleType: this.ADMIN_ROLE_TYPE,
        type: '2',
        userType: this.SUPER_USER_TYPE,
        parentClubLogo: element.Logo,
        parentClubKey: element.Key,
        password: element.ParentClubAdminPassword,
      });
    });
    this.allParentClub.set(clubs);
  }

  private handleUserLogin(user: AuthUserEntity): void {
    if (user.userType === this.SUPER_USER_TYPE) {
      this.handleSuperUserLogin(user);
    } else {
      this.handleStandardUserLogin(user);
    }
  }
}
